import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  useConfigurations,
  useUserConfigurations,
} from "@via/api/configuration";
import { Spinner } from "@via/components/loading/loading.tsx";
import { useAuth } from "@via/hooks/useAuth";
import {
  useCreateRunEnvironment,
  useGetSavedCredentials,
  useTestSSHConnection,
} from "@via/pages/account/profile/profile.api.tsx";
import { useAgentStatus } from "@via/pages/account/profile/run-environments/agent.api.tsx";
import { useDataRoots } from "@via/pages/account/profile/run-environments/data-roots.api.tsx";
import {
  AwsAuthMode,
  deriveAwsAuthMode,
  resolveAwsAuthPayload,
} from "@via/pages/account/profile/run-environments/run-environment-form/aws-auth-mode.ts";
import {
  type CardKey,
  type CardOpenState,
  initialCardState,
  rememberCardState,
} from "@via/pages/account/profile/run-environments/run-environment-form/card-open-state.ts";
import { CardShell } from "@via/pages/account/profile/run-environments/run-environment-form/card-shell.tsx";
import { DataFoldersCard } from "@via/pages/account/profile/run-environments/run-environment-form/cards/data-folders-card.tsx";
import {
  AppsCard,
  appsCardSummary,
} from "@via/pages/account/profile/run-environments/run-environment-form/cards/apps-card.tsx";
import {
  ContainerExecutionCard,
  containerExecutionCardSummary,
  containerExecutionShown,
} from "@via/pages/account/profile/run-environments/run-environment-form/cards/container-execution-card.tsx";
import { useContainerRuntime } from "@via/pages/account/profile/run-environments/container-runtime.api.tsx";
import {
  dataFoldersCardSummary,
  suggestedDataFolderRows,
} from "@via/pages/account/profile/run-environments/run-environment-form/data-folders.ts";
import { withWorkspaceBase } from "@via/pages/account/profile/run-environments/run-environment-form/workspace-directory";
import {
  AccessCard,
  accessCardSummary,
} from "@via/pages/account/profile/run-environments/run-environment-form/cards/access-card.tsx";
import {
  AgentCard,
  agentCardSummary,
} from "@via/pages/account/profile/run-environments/run-environment-form/cards/agent-card.tsx";
import {
  BasicsCard,
  basicsCardSummary,
} from "@via/pages/account/profile/run-environments/run-environment-form/cards/basics-card.tsx";
import {
  CloudCredentialsCard,
  cloudCredentialsCardSummary,
} from "@via/pages/account/profile/run-environments/run-environment-form/cards/cloud-credentials-card.tsx";
import {
  ConnectionCard,
  connectionCardSummary,
} from "@via/pages/account/profile/run-environments/run-environment-form/cards/connection-card.tsx";
import {
  ContainerCard,
  containerCardSummary,
} from "@via/pages/account/profile/run-environments/run-environment-form/cards/container-card.tsx";
import {
  DirectoriesCard,
  directoriesCardSummary,
} from "@via/pages/account/profile/run-environments/run-environment-form/cards/directories-card.tsx";
import {
  ExecutionCard,
  executionCardSummary,
} from "@via/pages/account/profile/run-environments/run-environment-form/cards/execution-card.tsx";
import {
  VariablesCard,
  variablesCardSummary,
} from "@via/pages/account/profile/run-environments/run-environment-form/cards/variables-card.tsx";
import {
  ContainerTypeOptions,
  DefaultValues,
  ExecutorOptions,
} from "@via/pages/account/profile/run-environments/run-environment-form/run-environment-constants.ts";
import { useMembershipAssumeRoleConnections } from "@via/pages/organization/membership-connections.tsx";
import { removeFalsyValues } from "@via/utils/helpers.tsx";
import {
  ContainerType,
  CredentialView,
  ExecutorJobsType,
  ExecutorType,
  ProfileClusterForClient,
} from "common";
import { JSX, useEffect, useRef, useState } from "react";
import { type FieldErrors, useForm } from "react-hook-form";
import {
  hasActiveMembership,
  requiresRoleBasedAwsAuth,
} from "@via/hooks/organization-access.ts";

/**
 * Which card each validated field lives in. A collapsed card is hidden with a
 * class rather than unmounted, so a message rendered inside a shut card is
 * invisible. Extend this whenever a rule is added to a field, otherwise a
 * blocked save looks like a dead Save button.
 */
const CARD_FOR_FIELD: Partial<Record<string, CardKey>> = {
  name: "basics",
  username: "connection",
};

/**
 * RunEnvironmentForm Component
 *
 * This component handles both creating a new run environment and editing an existing one.
 *
 * Props:
 * - setOpen: A function to control the visibility of the form on drawer component.
 * - editableRunEnvironment: An optional object representing the run environment being edited.
 *   - If this prop is provided, the form is in editing mode and will be populated with the existing data.
 *   - If this prop is not provided, the form is in creation mode for a new run environment.
 * - setEditableRunEnvironment: Setter function for editableRunEnvironment
 * - publicRunEnvironments: publicRunEnvironments is a list of templates to populate the form with template values (only available when creating a new run environment)
 *
 * The form will:
 * - Populate its fields with data from editableRunEnvironment when in editing mode.
 * - Populate its fields with data from selected publicRunEnvironments when user selects one in creating mode
 * - Allow the user to modify this data or enter new data when creating a new item.
 */
export default function RunEnvironmentForm({
  setOpen,
  editableRunEnvironment,
  setEditableRunEnvironment,
  publicRunEnvironments,
  isPublic = false,
  sshOnly = false,
  onSuccess,
  focusAgentCard = false,
}: {
  setOpen: (open: boolean) => void;
  editableRunEnvironment?: ProfileClusterForClient;
  setEditableRunEnvironment?: (
    editableRunEnvironment: ProfileClusterForClient
  ) => void;
  publicRunEnvironments?: ProfileClusterForClient[];
  isPublic?: boolean;
  /**
   * Set by a surface that can only use an ssh environment. It hides the
   * transport choice and pins the saved transport to ssh. See the prop of the
   * same name on BasicsCard for why offering Agent there is a dead choice.
   * Issue #472.
   */
  sshOnly?: boolean;
  onSuccess?: (id: number) => void;
  /**
   * Set when a deep link opened this form (agent-4-2): open the Agent card and scroll to it once the
   * environment has hydrated as an agent one. Not a user choice, so it is never remembered.
   */
  focusAgentCard?: boolean;
}): JSX.Element {
  /**
   * "draft" is the pre-populated state for run environment form
   */
  const [draft, setDraft] = useState<ProfileClusterForClient>();
  const [selectedExecutor, setSelectedExecutor] = useState<ExecutorType>(
    ExecutorType.Local
  );
  const [selectedProxy, setSelectedProxy] = useState("none");
  const [selectedExecutorJobs, setSelectedExecutorJobs] =
    useState<ExecutorJobsType>(ExecutorJobsType.Local);
  const [selectedSshId, setSelectedSshId] = useState<number>();
  const [selectedContainerType, setSelectedContainerType] =
    useState<ContainerType>(ContainerTypeOptions[0].value);
  const [selectedAmazonId, setSelectedAmazonId] = useState<number>();
  const [awsAuthMode, setAwsAuthMode] = useState<AwsAuthMode>(
    AwsAuthMode.StaticKey
  );
  const awsAuthModeUserTouchedRef = useRef(false);
  const [selectedGoogleId, setSelectedGoogleId] = useState<number>();
  const [selectedWriteGroupIds, setSelectedWriteGroupIds] = useState<number[]>(
    editableRunEnvironment?.writeGroupIds ?? []
  );
  const [selectedPublicRunEnvironmentId, setSelectedPublicRunEnvironmentId] =
    useState<number>();

  useEffect(() => {
    const publicRunEnvironment = publicRunEnvironments?.find(
      item => item.id === selectedPublicRunEnvironmentId
    );

    if (publicRunEnvironment && setEditableRunEnvironment) {
      setEditableRunEnvironment(publicRunEnvironment);
    }
  }, [selectedPublicRunEnvironmentId]);

  const { data: savedCredentials, isLoading: isSavedCredentialsLoading } =
    useGetSavedCredentials({ type: CredentialView.UserOwned });
  const { user } = useAuth();
  const requireRoleBasedAwsAuth = requiresRoleBasedAwsAuth(user);
  // Every active membership's assume role connections, labeled by organization (#1135).
  const {
    connections: assumeRoleCredentials,
    failedOrganizationNames: failedAssumeRoleOrganizations,
  } = useMembershipAssumeRoleConnections(user, {
    enabled: selectedExecutorJobs === ExecutorJobsType.AwsBatch,
  });
  const assumeRoleCredentialOptions = assumeRoleCredentials.map(c => ({
    label: c.label,
    value: c.id,
  }));
  const { mutate: testSSHConnection, isPending: isTestSSHConnectionLoading } =
    useTestSSHConnection();
  const sshCredentials =
    (savedCredentials?.ssh || []).map(({ id, name }) => ({
      label: name,
      value: id,
    })) ?? [];

  const amazonCredentials =
    (savedCredentials?.amazonCredentials || []).map(({ id, name }) => ({
      label: name,
      value: id,
    })) ?? [];

  const googleCredentials =
    (savedCredentials?.googleCredentials || []).map(({ id, name }) => ({
      label: name,
      value: id,
    })) ?? [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    reset,
    watch,
  } = useForm<ProfileClusterForClient>({
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: editableRunEnvironment ? draft : DefaultValues,
  });

  const { data: configurations } = useConfigurations();

  // Watch form fields for reactive button state and for the card summaries.
  // One subscription to the whole form, not one per field.
  const values = watch();
  const watchedUsername = values.username;
  const watchedHostname = values.hostname;
  // Read by the Agent card, to decide whether it renders at all.
  const watchedTransport = values.transport;

  /**
   * The id of an environment that actually exists on the server, which is what the Agent card
   * attaches to and what the status poll reads.
   *
   * ⚠️ Read from the prop, never from watch() or getValues(). This form is created with
   * shouldUnregister: true and id is not a registered field, so reset() rebuilds the form values
   * from registered fields alone and id is never among them. Both readers return undefined
   * forever, which left the card permanently showing "Save this run environment first" and the
   * status poll permanently disabled, even when editing a saved agent environment.
   *
   * ⚠️ !selectedPublicRunEnvironmentId is load bearing, not tidiness. Picking a public template
   * hydrates editableRunEnvironment with the TEMPLATE's row, id included, while nothing has been
   * saved yet. Handing that id to the card would offer Attach against an environment this user
   * does not own; an ordinary user is refused by the owner-scoped lookup, but the admin path
   * drops the ownership clause and the attach would land on the template itself.
   */
  const savedEnvironmentId = selectedPublicRunEnvironmentId
    ? undefined
    : editableRunEnvironment?.id;

  // Polled once here, then handed to the card below, so the card and its own collapsed summary
  // read the same status and a stale summary can never sit next to a card that has moved on.
  // One subscription rather than one in each.
  //
  // ⚠️ Gated on the transport, not just on enabled: Boolean(environmentId) inside the hook. Every
  // environment ever saved has an id, ssh ones included, and this hook runs on every render of
  // the form whatever the transport. Passing the id unconditionally would poll a real agent
  // status endpoint for plain ssh environments too, on every edit, for something no card shows.
  const { data: agentStatus, isError: isAgentStatusError } = useAgentStatus(
    "agent" === watchedTransport ? savedEnvironmentId : undefined
  );

  // The Data folders card ships dark: it appears on an agent environment only when the deployment
  // turned AGENT_RUNS_ENABLED on (spec 2026-09-28, section 6), and on an SSH environment only while
  // AGENT_WORKSPACE_JOBS_ENABLED is on, since its workspace jobs need data folders to mount
  // (agent-7-4). Polled once here for the collapsed summary; the card reads the same cached query.
  const { data: userConfigurations } = useUserConfigurations();
  const dataFoldersShown =
    ("agent" === watchedTransport &&
      Boolean(userConfigurations?.AGENT_RUNS_ENABLED)) ||
    ("ssh" === watchedTransport &&
      Boolean(userConfigurations?.AGENT_WORKSPACE_JOBS_ENABLED));
  const { data: savedDataRoots } = useDataRoots(
    dataFoldersShown ? savedEnvironmentId : undefined
  );

  // The Apps card ships dark too: it appears only while HPC_APPS_ENABLED is on (agent-9-1). On an
  // SSH environment it holds one line and reads nothing; the runtime GET is for agent ones only.
  const appsShown = Boolean(userConfigurations?.HPC_APPS_ENABLED);
  // The Container execution card ships dark as well: only while AGENT_WORKSPACE_JOBS_ENABLED is
  // on (agent-7-4), on agent and SSH environments. It shares the runtime GET with the Apps card.
  const containerExecutionVisible = containerExecutionShown(
    userConfigurations?.AGENT_WORKSPACE_JOBS_ENABLED,
    watchedTransport
  );
  const {
    data: savedContainerRuntime,
    isLoading: isContainerRuntimeLoading,
    error: containerRuntimeError,
  } = useContainerRuntime(
    (appsShown && "agent" === watchedTransport) || containerExecutionVisible
      ? savedEnvironmentId
      : undefined
  );
  const {
    data: containerExecutionRoots,
    isLoading: isContainerExecutionRootsLoading,
  } = useDataRoots(containerExecutionVisible ? savedEnvironmentId : undefined);

  // A host being set is what makes a username and an SSH key mandatory, so the
  // predicate reads the watched value and flips back the moment it is cleared.
  const hasHost = Boolean(watchedHostname?.trim());

  // The server already refuses to change transport on an existing
  // environment, so this only disables the Basics card's control as a hint
  // of that rule, not the enforcement of it. Derived from savedEnvironmentId
  // above so the disabled control and the Agent card can never disagree about
  // which environments count as saved.
  const isExisting = Boolean(savedEnvironmentId);
  const [sshIdError, setSshIdError] = useState<string | null>(null);

  // One source of truth for whether the ssh picker exists. The card's render
  // gate, the submit predicate and the message all read this, so a change to
  // where the picker appears cannot leave the submit gate behind and block a
  // save behind a message with nowhere to render.
  const sshPickerShown = !isPublic;

  // Membership, not truthiness. A public template hydrates selectedSshId with
  // the key its author used, and that id is not in this user's own list, so
  // the picker renders empty while a truthiness check waves the save through
  // to a launch that cannot authenticate.
  //
  // Nothing selected is missing whatever the fetch is doing. A selection that
  // is merely absent from a list still arriving is not judged at all, so a
  // slow credentials fetch cannot accuse a user whose key is about to appear.
  const sshKeyNotOwned =
    !selectedSshId ||
    (!isSavedCredentialsLoading &&
      !sshCredentials.some(({ value }) => value === selectedSshId));

  // One predicate for the submit gate and for the message. Two copies could
  // drift into raising an error that the render then decides to hide, which is
  // a save that stalls with nothing on screen to explain it.
  const sshKeyMissing = hasHost && sshPickerShown && sshKeyNotOwned;

  // Defense in depth alongside the payload guard in handleCreateProfile: a
  // key chosen before the user discovers Agent should not linger in state
  // either, even though it is the payload guard, not this, that guarantees
  // an agent environment never saves with one.
  useEffect(() => {
    if (watchedTransport === "agent") {
      setSelectedSshId(undefined);
    }
  }, [watchedTransport]);

  // The Cloud credentials card exists only for a cloud batch job executor.
  // Derived once so the render gate and the reveal effect read the same rule.
  const cloudCardShown =
    selectedExecutorJobs === ExecutorJobsType.AwsBatch ||
    selectedExecutorJobs === ExecutorJobsType.GoogleBatch;

  /**
   * If selectedExecutor is "local", user can pick anything for selectedExecutorJobs.
   * If selectedExecutor is not "local", selectedExecutorJobs is automatically selected and cannot be changed
   */
  const isExecutorJobsSelectorDisabled =
    selectedExecutor !== ExecutorOptions[0].value;

  const isEdit = Boolean(editableRunEnvironment?.id);
  const [cardOpen, setCardOpen] = useState<CardOpenState>(() =>
    initialCardState(isEdit)
  );

  const toggleCard = (key: CardKey): void => {
    setCardOpen(previous => {
      const next = { ...previous, [key]: !previous[key] };
      rememberCardState(next);
      return next;
    });
  };

  /**
   * Open a card the user did not open, because it holds a message they have to
   * act on. Editing starts with Connection collapsed, so without this a
   * blocked save shows nothing at all. It is not a user preference, so unlike
   * toggleCard it is deliberately not remembered.
   */
  const revealCard = (key: CardKey): void => {
    setCardOpen(previous =>
      previous[key] ? previous : { ...previous, [key]: true }
    );
  };

  useEffect(() => {
    if (!focusAgentCard || "agent" !== watchedTransport) return;
    setCardOpen(previous =>
      previous.agent ? previous : { ...previous, agent: true }
    );
    document
      .querySelector<HTMLElement>('[data-testid="card-agent"]')
      ?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [focusAgentCard, watchedTransport]);

  const revealCardsWithErrors = (
    formErrors: FieldErrors<ProfileClusterForClient>
  ): void => {
    for (const field of Object.keys(formErrors)) {
      const card = CARD_FOR_FIELD[field];
      if (card) revealCard(card);
    }
  };

  /**
   * Show the Cloud credentials card the first time the user's own choice of
   * job executor brings it into existence. Without this it materializes shut,
   * behind a summary, one click away from the credential the user just asked
   * for.
   *
   * Two guards. Hydrating an existing environment also flips the predicate,
   * and that is not a user action, so the card the user last shut stays shut
   * on every edit. And it reveals once, so a user who deliberately collapses
   * it afterwards is not fought on the next render.
   */
  const executorUserTouchedRef = useRef(false);
  const cloudCardRevealedRef = useRef(false);
  useEffect(() => {
    if (!cloudCardShown) return;
    if (!executorUserTouchedRef.current) return;
    if (cloudCardRevealedRef.current) return;
    cloudCardRevealedRef.current = true;
    revealCard("cloud");
  }, [cloudCardShown]);

  /**
   * Same rule, same two guards, for the Agent card. Choosing Agent is the point
   * at which attaching an agent becomes the next thing the user has to do, so
   * the card materializing shut behind a summary hides exactly what they just
   * asked for.
   *
   * The transport is a registered field, so it also changes when an existing
   * environment hydrates into the drawer. That is not a user action, which is
   * why the touched ref is set from the radio's own change handler and nowhere
   * else: opening a saved agent environment leaves the card as the user last
   * left it, the same way the Cloud card behaves on an existing cloud
   * environment.
   */
  const transportUserTouchedRef = useRef(false);
  const agentCardRevealedRef = useRef(false);
  useEffect(() => {
    if ("agent" !== watchedTransport) return;
    if (!transportUserTouchedRef.current) return;
    if (agentCardRevealedRef.current) return;
    agentCardRevealedRef.current = true;
    revealCard("agent");
  }, [watchedTransport]);

  /**
   * If the form component is called for editing, set the editing data to the draft state
   * and update the selected executor and executor jobs.
   */
  useEffect(() => {
    awsAuthModeUserTouchedRef.current = false;
    // A fresh environment loaded into the open drawer, from the public
    // template picker for instance, hydrates rather than being chosen, so the
    // reveals have to stand down for it the same way they do on mount.
    executorUserTouchedRef.current = false;
    transportUserTouchedRef.current = false;
    editableRunEnvironment?.executor &&
      setSelectedExecutor(editableRunEnvironment.executor);
    editableRunEnvironment?.executorJob &&
      setSelectedExecutorJobs(editableRunEnvironment.executorJob);
    // handleCreateProfile writes payload.proxy unconditionally from this state.
    // Without this line the state keeps its initial "none", so editing any
    // environment configured with an HTTP proxy silently cleared it on save.
    editableRunEnvironment?.proxy &&
      setSelectedProxy(editableRunEnvironment.proxy);
    editableRunEnvironment?.sshId &&
      setSelectedSshId(editableRunEnvironment.sshId);
    editableRunEnvironment?.containerType &&
      setSelectedContainerType(editableRunEnvironment.containerType);
    editableRunEnvironment?.amazonCreId &&
      setSelectedAmazonId(editableRunEnvironment.amazonCreId);
    editableRunEnvironment?.googleCreId &&
      setSelectedGoogleId(editableRunEnvironment.googleCreId);
    editableRunEnvironment?.writeGroupIds &&
      setSelectedWriteGroupIds(editableRunEnvironment.writeGroupIds);

    /**
     * Assign editableRunEnvironment to draft on editing mode.
     * Decode and assign "variable" and "bashVariable" values on editing mode.
     */
    if (editableRunEnvironment) {
      const { bashVariable, variable, hideWorkdir, runCommand, ...rest } =
        editableRunEnvironment;

      const updatedRunEnvironment: ProfileClusterForClient = {
        hideWorkdir,
        ...rest,
      };

      if (bashVariable) {
        updatedRunEnvironment.bashVariable = bashVariable;
      }

      if (variable) {
        updatedRunEnvironment.variable = variable;
      }

      if (runCommand) {
        updatedRunEnvironment.runCommand = runCommand;
      }

      updatedRunEnvironment.hideWorkdir = !!(
        hideWorkdir === "1" || hideWorkdir === "on"
      );

      setDraft(updatedRunEnvironment);
    }
  }, [editableRunEnvironment]);

  useEffect(() => {
    if (!editableRunEnvironment) return;
    if (awsAuthModeUserTouchedRef.current) return;
    setAwsAuthMode(
      deriveAwsAuthMode({
        awsUseInstanceRole: editableRunEnvironment.awsUseInstanceRole,
        amazonCreId: editableRunEnvironment.amazonCreId,
        assumeRoleCredentialIds: assumeRoleCredentials.map(c => c.id),
      })
    );
  }, [editableRunEnvironment, assumeRoleCredentials]);
  /**
   * Reset form state to inject default values
   */
  useEffect(() => {
    reset(draft);
  }, [draft]);

  const { mutateAsync: createProfile, isPending: isCreateProfileLoading } =
    useCreateRunEnvironment();

  const handleCreateProfile = async (
    payload: ProfileClusterForClient
  ): Promise<void> => {
    payload.containerType = selectedContainerType;
    payload.executor = selectedExecutor;
    payload.executorJob = selectedExecutorJobs;
    payload.proxy = selectedProxy;
    // ⚠️ Not cosmetic, and not redundant with hiding the radios. This form
    // uses shouldUnregister: true, so an input that never rendered is never
    // registered and transport is simply absent from the submitted values.
    // The server then falls back to its own 'ssh' default, which happens to
    // be right today but leaves the surface trusting a default it does not
    // state. Pin it here so the payload says what this surface means.
    if (sshOnly) payload.transport = "ssh";
    // An agent environment must never carry an ssh key, whatever the user
    // clicked before switching transport. selectedSshId is component state,
    // so unmounting the ssh picker cannot clear it on its own: this is the
    // guarantee itself, not just a UI hint, so it is enforced here at the
    // payload boundary rather than only by what the Connection card renders.
    selectedSshId &&
      payload.transport !== "agent" &&
      (payload.sshId = selectedSshId);
    const awsAuth = resolveAwsAuthPayload(awsAuthMode, selectedAmazonId);
    payload.awsUseInstanceRole = awsAuth.awsUseInstanceRole;
    if (awsAuth.amazonCreId !== undefined) {
      // Static key and assume role persist the chosen amazon_credentials row.
      // Instance role omits amazonCreId so the server clears it; the launch path
      // honors the instance role flag only when no credential is attached.
      payload.amazonCreId = awsAuth.amazonCreId;
    }
    selectedGoogleId && (payload.googleCreId = selectedGoogleId);
    payload.writeGroupIds = selectedWriteGroupIds;

    /**
     * hideWorkdir is booleans. However, backend expects it in string format (example: "1" or "0")
     */
    payload.hideWorkdir = Number(payload.hideWorkdir ?? false).toString();

    /**
     * If form is in editing mode add id to payload
     * else add isPublic option to payload.
     */
    if (!selectedPublicRunEnvironmentId && editableRunEnvironment?.id) {
      payload.id = editableRunEnvironment.id;
      payload.public = Boolean(editableRunEnvironment.public);
    } else {
      payload.public = isPublic;
    }

    const { profileId } = await createProfile({
      // The workspace directory is put back after removeFalsyValues, so a
      // cleared field reaches the server as null (the default) instead of
      // being dropped and leaving the stored value in place.
      profile: withWorkspaceBase(
        removeFalsyValues(payload),
        payload.transport ?? watchedTransport,
        payload.workspaceBase
      ),
    });
    if (profileId) {
      onSuccess && onSuccess(profileId);
      profileId && setOpen(false);
    }
  };

  const handleExecutorChange = (executor: any) => {
    executorUserTouchedRef.current = true;
    setSelectedExecutor(executor.value);

    /**
     * If executor is not local, selectedExecutorJobs needs to be same as selectedExecutor
     */
    if (executor.value !== ExecutorOptions[0].value) {
      setSelectedExecutorJobs(executor.value);
    }
  };

  const handleExecutorJobsChange = (executorJobs: any) => {
    executorUserTouchedRef.current = true;
    setSelectedExecutorJobs(executorJobs.value);
  };

  const handleProxyChange = (proxy: any) => {
    setSelectedProxy(proxy.value);
  };

  const handleSshIdChange = (sshId: any) => {
    setSelectedSshId(sshId.value);
  };

  const handleAwsIdChange = (amazonId: any) => {
    setSelectedAmazonId(amazonId?.value);
  };

  /**
   * Switching modes clears the selected credential so a static id can never
   * leak into assume mode or vice versa.
   */
  const handleAwsAuthModeChange = (mode: AwsAuthMode) => {
    awsAuthModeUserTouchedRef.current = true;
    setAwsAuthMode(mode);
    setSelectedAmazonId(undefined);
  };

  const handleGoogleIdChange = (googleId: any) => {
    setSelectedGoogleId(googleId?.value);
  };

  const handleContainerTypeChange = (containerType: any) => {
    setSelectedContainerType(containerType.value);
  };

  /**
   * Only the radio in the Basics card calls this. Hydration writes the same
   * field through reset(), which fires no change event, so the reveal above
   * can tell a choice apart from a row being loaded.
   */
  const handleTransportUserChange = (): void => {
    transportUserTouchedRef.current = true;
  };

  const handlePublicTemplateChange = (publicTemplate: any) => {
    setSelectedPublicRunEnvironmentId(publicTemplate?.value);
  };

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        // The gate is a hostname being set, not the executor. An environment
        // with a host reaches it over SSH whatever the executor, and an LSF
        // environment with no host is a legitimate submit-host configuration.
        //
        // sshId is component state, not a registered field, so it cannot carry
        // a react-hook-form rule. Registering it would add a key to the saved
        // payload, so it is checked here instead. React-hook-form still runs,
        // so the username error lights up in the same click rather than on the
        // next one.
        // A public template hides the ssh picker, so the key cannot be
        // required there: the message would have nowhere to render and the
        // save would stall with no visible reason. A collapsed card hides a
        // message the same way, which is why a blocked save opens the card
        // that holds it. sshKeyMissing carries both of those rules and is the
        // same value the message is rendered from.
        setSshIdError(sshKeyMissing ? "An SSH key is required" : null);
        if (sshKeyMissing) {
          revealCard("connection");
          void handleSubmit(() => undefined, revealCardsWithErrors)(e);
          return;
        }
        void handleSubmit(handleCreateProfile, revealCardsWithErrors)(e);
      }}
      onKeyDown={e => {
        // Enter in a text input is stopped so a stray keystroke cannot submit
        // a half-filled drawer. A button is different: Enter activates it
        // through that same default action, so cancelling it would kill every
        // card toggle and the Save button for anyone working by keyboard.
        const tag = (e.target as HTMLElement).tagName;
        if (e.key === "Enter" && tag !== "TEXTAREA" && tag !== "BUTTON") {
          e.preventDefault(); // Stop the form from submitting
        }
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              {editableRunEnvironment
                ? "Edit Run Environment"
                : "New Run Environment"}
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={() => {
                  setOpen(false);
                }}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">
              {editableRunEnvironment
                ? "Edit your run environment details below."
                : "Get started by filling in the information below to create your new run environment."}
            </p>
          </div>
        </div>

        <div className="space-y-4 px-4 py-6 sm:px-6">
          <CardShell
            title="Basics"
            testId="basics"
            summary={basicsCardSummary({ name: values.name })}
            open={cardOpen.basics}
            onToggle={() => toggleCard("basics")}>
            <BasicsCard
              register={register}
              errors={errors}
              publicRunEnvironments={publicRunEnvironments}
              onTemplateChange={handlePublicTemplateChange}
              onTransportUserChange={handleTransportUserChange}
              isExisting={isExisting}
              sshOnly={sshOnly}
              transport={watchedTransport}
            />
          </CardShell>

          <CardShell
            title="Connection"
            testId="connection"
            summary={connectionCardSummary({
              username: values.username,
              hostname: values.hostname,
              port: values.port,
              sshKeyLabel: sshCredentials.find(
                ssh => ssh.value === selectedSshId
              )?.label,
            })}
            open={cardOpen.connection}
            onToggle={() => toggleCard("connection")}>
            <ConnectionCard
              register={register}
              errors={errors}
              sshPickerShown={sshPickerShown}
              selectedProxy={selectedProxy}
              onProxyChange={handleProxyChange}
              sshCredentials={sshCredentials}
              isSavedCredentialsLoading={isSavedCredentialsLoading}
              selectedSshId={selectedSshId}
              onSshIdChange={handleSshIdChange}
              getValues={getValues}
              testSSHConnection={testSSHConnection}
              isTestSSHConnectionLoading={isTestSSHConnectionLoading}
              watchedUsername={watchedUsername}
              watchedHostname={watchedHostname}
              hasHost={hasHost}
              transport={watchedTransport}
              // The message is raised on submit, so it is shown only while the
              // condition that raised it still holds. Clearing the host or
              // picking a key takes it away at once rather than on next Save.
              sshIdError={sshKeyMissing ? sshIdError : null}
            />
          </CardShell>

          {"agent" === watchedTransport ? (
            <CardShell
              title="Agent"
              testId="agent"
              summary={agentCardSummary(agentStatus)}
              open={cardOpen.agent}
              onToggle={() => toggleCard("agent")}>
              <AgentCard
                environmentId={savedEnvironmentId}
                status={agentStatus}
                statusError={isAgentStatusError}
                hpcAppsEnabled={appsShown}
              />
            </CardShell>
          ) : null}

          {dataFoldersShown ? (
            <CardShell
              title="Data folders"
              testId="data-folders"
              summary={dataFoldersCardSummary(savedDataRoots)}
              open={cardOpen.dataFolders}
              onToggle={() => toggleCard("dataFolders")}>
              <DataFoldersCard
                environmentId={savedEnvironmentId}
                transport={watchedTransport}
                suggestions={suggestedDataFolderRows(values)}
              />
            </CardShell>
          ) : null}

          {containerExecutionVisible ? (
            <CardShell
              title="Container execution"
              testId="container-execution"
              summary={containerExecutionCardSummary(
                savedContainerRuntime,
                containerExecutionRoots,
                {
                  loading:
                    isContainerRuntimeLoading ||
                    isContainerExecutionRootsLoading,
                  error: containerRuntimeError,
                }
              )}
              open={cardOpen.containerExecution}
              onToggle={() => toggleCard("containerExecution")}>
              <ContainerExecutionCard
                environmentId={savedEnvironmentId}
                borrowed={Boolean(editableRunEnvironment?.sealedByOwnerName)}
                siteDefaults={{
                  modules:
                    userConfigurations?.CONTAINER_RUNTIME_DEFAULT_MODULES ?? [],
                  image:
                    userConfigurations?.CONTAINER_RUNTIME_DEFAULT_IMAGE ?? null,
                }}
              />
            </CardShell>
          ) : null}

          {appsShown ? (
            <CardShell
              title="Apps"
              testId="apps"
              summary={
                "agent" === watchedTransport
                  ? appsCardSummary(savedContainerRuntime)
                  : "Agent environments only"
              }
              open={cardOpen.apps}
              onToggle={() => toggleCard("apps")}>
              <AppsCard
                environmentId={savedEnvironmentId}
                transport={watchedTransport}
                ownerId={editableRunEnvironment?.ownerId}
                currentUserId={user?.id}
              />
            </CardShell>
          ) : null}

          <CardShell
            title="Execution"
            testId="execution"
            summary={executionCardSummary({
              selectedExecutor,
              selectedExecutorJobs,
            })}
            open={cardOpen.execution}
            onToggle={() => toggleCard("execution")}>
            <ExecutionCard
              register={register}
              selectedExecutor={selectedExecutor}
              onExecutorChange={handleExecutorChange}
              selectedExecutorJobs={selectedExecutorJobs}
              onExecutorJobsChange={handleExecutorJobsChange}
              isExecutorJobsSelectorDisabled={isExecutorJobsSelectorDisabled}
            />
          </CardShell>

          {cloudCardShown ? (
            <CardShell
              title="Cloud credentials"
              testId="cloud"
              summary={cloudCredentialsCardSummary({
                selectedExecutorJobs,
                awsAuthMode,
                // Static keys and assume-role credentials are separate id
                // spaces, so the list is picked by mode. Concatenating them
                // lets a colliding id name the wrong credential.
                credentialLabel:
                  awsAuthMode === AwsAuthMode.AssumeRole
                    ? assumeRoleCredentialOptions.find(
                        o => o.value === selectedAmazonId
                      )?.label
                    : awsAuthMode === AwsAuthMode.StaticKey
                      ? amazonCredentials.find(
                          o => o.value === selectedAmazonId
                        )?.label
                      : undefined,
                region: values.region,
              })}
              open={cardOpen.cloud}
              onToggle={() => toggleCard("cloud")}>
              <CloudCredentialsCard
                register={register}
                selectedExecutorJobs={selectedExecutorJobs}
                awsAuthMode={awsAuthMode}
                onAwsAuthModeChange={handleAwsAuthModeChange}
                requireRoleBasedAwsAuth={requireRoleBasedAwsAuth}
                canUseAssumeRole={hasActiveMembership(user)}
                amazonCredentials={amazonCredentials}
                assumeRoleCredentialOptions={assumeRoleCredentialOptions}
                failedOrganizationNames={failedAssumeRoleOrganizations}
                selectedAmazonId={selectedAmazonId}
                onAwsIdChange={handleAwsIdChange}
                googleCredentials={googleCredentials}
                selectedGoogleId={selectedGoogleId}
                onGoogleIdChange={handleGoogleIdChange}
                isSavedCredentialsLoading={isSavedCredentialsLoading}
              />
            </CardShell>
          ) : null}

          <CardShell
            title="Container"
            testId="container"
            summary={containerCardSummary({
              selectedContainerType,
              containerOptions: values.containerOptions,
            })}
            open={cardOpen.container}
            onToggle={() => toggleCard("container")}>
            <ContainerCard
              register={register}
              selectedContainerType={selectedContainerType}
              onContainerTypeChange={handleContainerTypeChange}
            />
          </CardShell>

          <CardShell
            title="Directories"
            testId="directories"
            summary={directoriesCardSummary({
              defWorkdir: values.defWorkdir,
              defPublishdir: values.defPublishdir,
            })}
            open={cardOpen.directories}
            onToggle={() => toggleCard("directories")}>
            <DirectoriesCard
              register={register}
              showWorkspaceDirectory={"agent" === watchedTransport}
              diskSpace={{
                runEnvironmentId: editableRunEnvironment?.id ?? null,
                defNextflowWorkdir: values.defNextflowWorkdir,
                defWorkdir: values.defWorkdir,
              }}
            />
          </CardShell>

          <CardShell
            title="Variables"
            testId="variables"
            summary={variablesCardSummary({
              variable: values.variable,
              bashVariable: values.bashVariable,
              runCommand: values.runCommand,
            })}
            open={cardOpen.variables}
            onToggle={() => toggleCard("variables")}>
            <VariablesCard
              register={register}
              showRunCommand={Boolean(configurations?.SHOW_RUN_COMMAND)}
            />
          </CardShell>

          <CardShell
            title="Access"
            testId="access"
            summary={accessCardSummary({
              writeGroupCount: selectedWriteGroupIds.length,
              isEdit,
            })}
            open={cardOpen.access}
            onToggle={() => toggleCard("access")}>
            <AccessCard
              runEnvironmentId={editableRunEnvironment?.id}
              selectedWriteGroupIds={selectedWriteGroupIds}
              onWriteGroupsChange={setSelectedWriteGroupIds}
            />
          </CardShell>
        </div>
      </div>

      {/* The form's divide-y already draws the rule above this footer, at the
          same width and colour, so a border of its own would double it. */}
      <div
        data-testid="run-environment-footer"
        className="flex flex-shrink-0 justify-end gap-4 px-4 py-4">
        <button
          id="run-environment-cancel"
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={() => {
            setOpen(false);
          }}>
          Cancel
        </button>
        <button
          id="run-environment-save"
          type="submit"
          className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <Spinner loading={isCreateProfileLoading}>Save</Spinner>
        </button>
      </div>
    </form>
  );
}
